import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {FiBarChart2, FiPlus, FiEdit2, FiTrash2, FiSearch, FiStar } from 'react-icons/fi';
import { getDrivers, createDriver, updateDriver, deleteDriver } from '../services/drivers';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import DriverForm from '../components/Drivers/DriverForm';
import toast from 'react-hot-toast';

const statusColors = {
  available: 'bg-green-100 text-green-800',
  busy: 'bg-yellow-100 text-yellow-800',
  inactive: 'bg-gray-100 text-gray-800',
  on_leave: 'bg-red-100 text-red-800',
};

export default function Drivers() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [filters, setFilters] = useState({ status: '', search: '', page: 1, limit: 20 });
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchDrivers();
  }, [filters]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const data = await getDrivers(filters);
      setDrivers(data.data || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (driverData) => {
    try {
      await createDriver(driverData);
      toast.success('Driver created successfully');
      setShowForm(false);
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to create driver');
    }
  };

  const handleUpdate = async (id, driverData) => {
    try {
      await updateDriver(id, driverData);
      toast.success('Driver updated successfully');
      setEditingDriver(null);
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to update driver');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) return;
    try {
      await deleteDriver(id);
      toast.success('Driver deleted successfully');
      fetchDrivers();
    } catch (error) {
      toast.error('Failed to delete driver');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Drivers</h1>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
        >
          <FiPlus className="mr-2" /> Add Driver
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="available">Available</option>
              <option value="busy">Busy</option>
              <option value="inactive">Inactive</option>
              <option value="on_leave">On Leave</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm text-gray-600 mb-1">Search</label>
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Name or phone number"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Drivers Table */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earnings</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {drivers.map((driver) => (
                  <tr key={driver._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-800">{driver.name}</p>
                    </td>
                    <td className="px-6 py-4 text-sm">{driver.phone}</td>
                    <td className="px-6 py-4 text-sm">
                      {driver.vehicle?.model ? `${driver.vehicle.model} (${driver.vehicle.license_plate})` : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${statusColors[driver.status]}`}>
                        {driver.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {driver.rating?.average > 0 ? (
                        <div className="flex items-center text-sm text-yellow-500">
                          <FiStar className="w-3 h-3 fill-current mr-1" />
                          <span>{driver.rating.average}</span>
                          <span className="text-gray-400 ml-1">({driver.rating.count})</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">${driver.earnings?.total || 0}</td>
                    <td className="px-6 py-4 text-right">
                      <button
  onClick={() => navigate(`/drivers/${driver._id}/stats`)}
  className="text-purple-500 hover:text-purple-700 mr-3"
  title="View Stats"
>
  <FiBarChart2 className="w-4 h-4" />
</button>
                      <button
                        onClick={() => setEditingDriver(driver)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(driver._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {drivers.length === 0 && (
            <div className="text-center py-12 text-gray-500">No drivers found</div>
          )}
          
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t">
              <Pagination
                currentPage={filters.page}
                totalPages={totalPages}
                onPageChange={(page) => setFilters({ ...filters, page })}
              />
            </div>
          )}
        </div>
      )}

      {/* Driver Form Modal */}
      <DriverForm
        isOpen={showForm || !!editingDriver}
        onClose={() => {
          setShowForm(false);
          setEditingDriver(null);
        }}
        onSubmit={editingDriver ? (data) => handleUpdate(editingDriver._id, data) : handleCreate}
        initialData={editingDriver}
      />
    </div>
  );
}
